/**
 * Page shell for the Resale Pricing section: one "Resale Pricing" page at
 * /resale whose subpages render as tabs (Overview, Price Rules, Overrides,
 * Condition Discounts, Rounding Rules) instead of separate sidebar entries.
 */
import SectionTabShell, { type SectionTab } from '@src/components/SectionTabShell';

export type ResalePricingTab =
  | 'overview'
  | 'pricingRules'
  | 'itemAdjustedPrices'
  | 'conditionDiscounts'
  | 'roundingRules';

const TABS: SectionTab<ResalePricingTab>[] = [
  { value: 'overview', label: 'Overview', path: '/resale' },
  { value: 'pricingRules', label: 'Price Rules', path: '/resale/pricing' },
  { value: 'itemAdjustedPrices', label: 'Overrides', path: '/resale/overrides' },
  { value: 'conditionDiscounts', label: 'Condition Discounts', path: '/resale/conditions' },
  { value: 'roundingRules', label: 'Rounding Rules', path: '/resale/rounding' },
];

const TAB_DESCRIPTIONS: Record<ResalePricingTab, string> = {
  overview: 'Understand how prices are calculated, then jump to the right configuration page.',
  pricingRules: 'Ordered rules that derive a resale price from a source price. The first matching rule wins.',
  itemAdjustedPrices: 'SKU-level price overrides currently in effect, uploaded as markdowns.',
  conditionDiscounts: 'Discounts applied on top of the resale price based on item condition.',
  roundingRules: 'How calculated prices round to their final storefront value.',
};

interface ResalePricingTabShellProps {
  tab: ResalePricingTab;
  children: React.ReactNode;
}

const ResalePricingTabShell = ({ tab, children }: ResalePricingTabShellProps) => (
  <SectionTabShell title="Resale Pricing" description={TAB_DESCRIPTIONS[tab]} tabs={TABS} tab={tab}>
    {children}
  </SectionTabShell>
);

export default ResalePricingTabShell;
